import { useState } from "react";
import {
  Box,
  Select,
  MenuItem,
  IconButton,
  Typography,
  TextField,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  ExpandMore,
} from "@mui/icons-material";
import typography from "../cssUtils/typography";

export default function CustomPagination({
  count,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [20, 50, 100],
}) {
  const totalPages = Math.ceil(count / rowsPerPage);
  const from = count === 0 ? 0 : page * rowsPerPage + 1;
  const to = Math.min(count, (page + 1) * rowsPerPage);
  const [jumpPage, setJumpPage] = useState("");

  const getPageNumbers = () => {
    const pages = [];

    if (totalPages <= 7) {
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(0);

      if (page <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages - 1);
      } else if (page >= totalPages - 4) {
        pages.push("ellipsis");
        for (let i = totalPages - 5; i < totalPages - 1; i++) {
          pages.push(i);
        }
        pages.push(totalPages - 1);
      } else {
        pages.push("ellipsis");
        for (let i = page - 1; i <= page + 1; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages - 1);
      }
    }

    return pages;
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        justifyContent: { xs: "center", md: "space-between" },
        alignItems: { xs: "start", md: "center" },
        gap: { xs: 1.5, md: 0 },
        padding: "12px 16px",
      }}
    >
      {/* Left: Items per page + range */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            ...typography.bodyLarge,
            color: "rgba(0, 0, 0, 0.6)",
            fontWeight: 400,
          }}
        >
          Items per page
        </Typography>
        <Select
          value={rowsPerPage}
          onChange={(e) => onRowsPerPageChange(e)}
          size="small"
          variant="outlined"
          IconComponent={ExpandMore}
          sx={{
            fontSize: "0.875rem",
            minWidth: 70,
            "& .MuiOutlinedInput-notchedOutline": { border: "none" },
            "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
            "&.Mui-focused": { backgroundColor: "transparent" },
            "& .MuiSelect-select": {
              paddingRight: "28px",
              paddingTop: "4px",
              paddingBottom: "4px",
              ...typography.bodyLarge,
              fontWeight: 400,
            },
          }}
        >
          {rowsPerPageOptions.map((option) => (
            <MenuItem key={option} value={option} sx={typography.bodyLarge}>
              {option}
            </MenuItem>
          ))}
        </Select>
        <Typography
          sx={{
            ...typography.bodyLarge,
            color: "rgba(0, 0, 0, 0.87)",
            ml: 2,
            fontWeight: 400,
          }}
        >
          {from} - {to} of {count}
        </Typography>
      </Box>

      {/* Right: Page navigation */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        <IconButton
          onClick={() => onPageChange(null, page - 1)}
          disabled={page === 0}
          size="small"
          sx={{ padding: "4px" }}
        >
          <ChevronLeft />
        </IconButton>

        {getPageNumbers().map((pageNum, index) => {
          if (pageNum === "ellipsis") {
            return (
              <Typography
                key={`ellipsis-${index}`}
                sx={{
                  ...typography.bodyLarge,
                  px: 1,
                  color: "rgba(0, 0, 0, 0.6)",
                  fontWeight: 400,
                }}
              >
                ...
              </Typography>
            );
          }

          return (
            <IconButton
              key={pageNum}
              onClick={() => onPageChange(null, pageNum)}
              size="small"
              sx={{
                padding: "4px 8px",
                minWidth: "32px",
                backgroundColor: page === pageNum ? "#EBF6FF" : "transparent",
                "&:hover": {
                  backgroundColor:
                    page === pageNum
                      ? "rgba(0, 0, 0, 0.12)"
                      : "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <Typography
                sx={{
                  ...typography.bodyLarge,
                  fontWeight: page === pageNum ? 600 : 400,
                }}
              >
                {pageNum + 1}
              </Typography>
            </IconButton>
          );
        })}

        <IconButton
          onClick={() => onPageChange(null, page + 1)}
          disabled={page >= totalPages - 1}
          size="small"
          sx={{ padding: "4px" }}
        >
          <ChevronRight />
        </IconButton>
        <Box sx={{ display: "flex", alignItems: "center", ml: 2, gap: 1 }}>
          <Typography sx={typography.bodyLarge}>
            Go to
          </Typography>

          <TextField
            size="small"
            value={jumpPage}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "");
              setJumpPage(value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const pageNumber = Number(jumpPage);

                if (
                  !Number.isNaN(pageNumber) &&
                  pageNumber >= 1 &&
                  pageNumber <= totalPages
                ) {
                  onPageChange(null, pageNumber - 1);
                  setJumpPage("");
                }
              }
            }}
            inputProps={{
              min: 1,
              max: totalPages,
              maxLength: String(totalPages).length,
              style: {
                textAlign: "center",
                padding: "6px 0",
              },
            }}
            sx={{
              width: 50,
              "& .MuiOutlinedInput-root": {
                height: 30,
              },
              "& input": {
                textAlign: "center",
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
